import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReqResService } from 'src/app/service/req-res.service';
import * as Chart from 'chart.js';
import * as XLSX from 'xlsx';

interface ReportOrderChartItem {
  price: number;
  segment: string;
  subSegment: string;
  date: string;
  orderCount: number;
  status: string;
  satusCSS: string;
}

interface SummaryRow {
  name: string;
  price: number;
  orderCount: number;
  rowCount: number;
}

@Component({
  selector: 'app-reportorderchart',
  templateUrl: './reportorderchart.component.html',
  styleUrls: ['./reportorderchart.component.css']
})
export class ReportorderchartComponent implements OnInit, OnDestroy {
  data: ReportOrderChartItem[] = [];
  filteredData: ReportOrderChartItem[] = [];
  summaryTableData: SummaryRow[] = [];

  beginDate: string = '';
  endDate: string = '';
  selectedStatus: number = 0;

  statusObj: any[] = [];

  months: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  priceChart: any = null;
  orderChart: any = null;

  priceChangeText: string = '-';
  priceChangeSubtext: string = '-';
  priceChangeClass: string = 'neutral';

  orderChangeText: string = '-';
  orderChangeSubtext: string = '-';
  orderChangeClass: string = 'neutral';

  isLoading = false;

  currentView: 'segment' | 'subsegment' = 'segment';
  selectedSegment: string | null = null;

  bluePalette: string[] = [
    '#1e3a8a',
    '#1d4ed8',
    '#2563eb',
    '#3b82f6',
    '#0284c7',
    '#0369a1',
    '#0ea5e9',
    '#38bdf8',
    '#4f46e5',
    '#4338ca',
    '#6366f1',
    '#818cf8',
    '#60a5fa',
    '#93c5fd',
    '#2563eb'
  ];

  constructor(private reqRes: ReqResService) { }

  ngOnInit(): void {
    this.setDefaultDates();
    this.fillStatusDropdown();
    this.getChartData();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  destroyCharts(): void {
    if (this.priceChart) {
      this.priceChart.destroy();
      this.priceChart = null;
    }

    if (this.orderChart) {
      this.orderChart.destroy();
      this.orderChart = null;
    }
  }

  setDefaultDates(): void {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    this.endDate = this.formatDateForInput(today);
    this.beginDate = this.formatDateForInput(oneMonthAgo);
  }

  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return year + '-' + month + '-' + day;
  }

  fillStatusDropdown(): void {
    this.reqRes.getData('/Report/GetSpecode/ORDER_STATUS').subscribe(
      (res: any) => {
        this.statusObj = Array.isArray(res) ? res : (res && res.data ? res.data : []);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  onFilterChange(): void {
  }

  onSearch(): void {
    this.currentView = 'segment';
    this.selectedSegment = null;
    this.getChartData();
  }

  getChartData(): void {
    if (!this.beginDate || !this.endDate) {
      return;
    }

    this.isLoading = true;

    const url = '/Report/GetOrderChartData/' + this.beginDate + '/' + this.endDate + '/' + this.selectedStatus;

    this.reqRes.getData(url).subscribe(
      (res: any) => {
        this.data = Array.isArray(res) ? res : (res && res.data ? res.data : []);
        this.applyCurrentViewData();
        this.renderCharts();
        this.isLoading = false;
      },
      (err: any) => {
        console.error('Chart data error:', err);
        this.data = [];
        this.filteredData = [];
        this.summaryTableData = [];
        this.renderCharts();
        this.isLoading = false;
      }
    );
  }

  applyCurrentViewData(): void {
    if (this.currentView === 'subsegment' && this.selectedSegment) {
      this.filteredData = this.data.filter(x => x.segment === this.selectedSegment);
    } else {
      this.filteredData = [...this.data];
    }

    this.filteredData.sort((a, b) => {
      return this.parseDateToDate(b.date).getTime() - this.parseDateToDate(a.date).getTime();
    });

    this.buildSummaryTable();
    this.updateKpi('price');
    this.updateKpi('order');
  }

  buildSummaryTable(): void {
    const map: { [key: string]: SummaryRow } = {};

    if (this.currentView === 'segment') {
      this.filteredData.forEach(item => {
        const key = item.segment || 'N/A';

        if (!map[key]) {
          map[key] = {
            name: key,
            price: 0,
            orderCount: 0,
            rowCount: 0
          };
        }

        map[key].price += Number(item.price || 0);
        map[key].orderCount += Number(item.orderCount || 0);
        map[key].rowCount += 1;
      });
    } else {
      this.filteredData.forEach(item => {
        const key = item.subSegment || 'N/A';

        if (!map[key]) {
          map[key] = {
            name: key,
            price: 0,
            orderCount: 0,
            rowCount: 0
          };
        }

        map[key].price += Number(item.price || 0);
        map[key].orderCount += Number(item.orderCount || 0);
        map[key].rowCount += 1;
      });
    }

    this.summaryTableData = Object.keys(map)
      .map(key => map[key])
      .sort((a, b) => b.price - a.price);
  }

  backToSegments(): void {
    this.currentView = 'segment';
    this.selectedSegment = null;
    this.applyCurrentViewData();
    this.renderCharts();
  }

  drillToSubsegment(segment: string): void {
    if (!segment) {
      return;
    }

    this.currentView = 'subsegment';
    this.selectedSegment = segment;
    this.applyCurrentViewData();
    this.renderCharts();
  }

  parseDate(dateStr: string): { day: number; month: number; year: number } {
    if (!dateStr) {
      return { day: 0, month: 0, year: 0 };
    }

    const parts = dateStr.split('/');

    return {
      day: Number(parts[0]),
      month: Number(parts[1]),
      year: Number(parts[2])
    };
  }

  parseDateToDate(dateStr: string): Date {
    const p = this.parseDate(dateStr);
    return new Date(p.year, p.month - 1, p.day);
  }

  getCurrentGroupingField(): 'segment' | 'subSegment' {
    return this.currentView === 'segment' ? 'segment' : 'subSegment';
  }

  getLabels(items: ReportOrderChartItem[]): string[] {
    const field = this.getCurrentGroupingField();
    const labels: string[] = [];

    items.forEach(x => {
      const val = (x as any)[field];
      if (val && labels.indexOf(val) === -1) {
        labels.push(val);
      }
    });

    return labels;
  }

  colorFromPalette(text: string): string {
    let hash = 0;

    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % this.bluePalette.length;
    return this.bluePalette[index];
  }

  prepareDatasets(metricField: 'price' | 'orderCount') {
    const labels = this.getLabels(this.filteredData);
    const monthGroupTotals: any = {};

    for (let m = 1; m <= 12; m++) {
      monthGroupTotals[m] = {};
      labels.forEach(label => {
        monthGroupTotals[m][label] = 0;
      });
    }

    const field = this.getCurrentGroupingField();

    this.filteredData.forEach(item => {
      const parsed = this.parseDate(item.date);
      const groupName = (item as any)[field];

      if (parsed.month >= 1 && parsed.month <= 12 && groupName) {
        monthGroupTotals[parsed.month][groupName] += Number((item as any)[metricField] || 0);
      }
    });

    return labels.map(label => ({
      label: label,
      data: Array.from({ length: 12 }, (_, i) => monthGroupTotals[i + 1][label] || 0),
      backgroundColor: this.colorFromPalette(label),
      borderColor: this.colorFromPalette(label),
      borderWidth: 1,
      borderRadius: 6,
      barPercentage: 0.78,
      categoryPercentage: 0.72
    }));
  }

  calculateChange(metricField: 'price' | 'orderCount') {
    const monthlyTotals = Array(12).fill(0);

    this.filteredData.forEach(item => {
      const parsed = this.parseDate(item.date);

      if (parsed.month >= 1 && parsed.month <= 12) {
        monthlyTotals[parsed.month - 1] += Number((item as any)[metricField] || 0);
      }
    });

    const firstIndex = monthlyTotals.findIndex(x => x > 0);

    let lastIndex = -1;
    for (let i = monthlyTotals.length - 1; i >= 0; i--) {
      if (monthlyTotals[i] > 0) {
        lastIndex = i;
        break;
      }
    }

    if (firstIndex === -1 || lastIndex === -1) {
      return null;
    }

    const firstValue = monthlyTotals[firstIndex];
    const lastValue = monthlyTotals[lastIndex];

    if (firstValue === 0) {
      return null;
    }

    const percent = ((lastValue - firstValue) / firstValue) * 100;

    return {
      firstMonth: this.months[firstIndex],
      lastMonth: this.months[lastIndex],
      firstValue: firstValue,
      lastValue: lastValue,
      percent: percent
    };
  }

  updateKpi(metric: 'price' | 'order'): void {
    const changeData = this.calculateChange(metric === 'price' ? 'price' : 'orderCount');

    if (!changeData) {
      if (metric === 'price') {
        this.priceChangeText = '-';
        this.priceChangeSubtext = 'No comparable month range';
        this.priceChangeClass = 'neutral';
      } else {
        this.orderChangeText = '-';
        this.orderChangeSubtext = 'No comparable month range';
        this.orderChangeClass = 'neutral';
      }
      return;
    }

    const sign = changeData.percent > 0 ? '+' : '';
    const text = sign + changeData.percent.toFixed(2) + '%';
    const subtext =
      changeData.firstMonth + ' (' +
      (metric === 'price' ? changeData.firstValue.toFixed(2) : changeData.firstValue.toFixed(0)) +
      ') → ' +
      changeData.lastMonth + ' (' +
      (metric === 'price' ? changeData.lastValue.toFixed(2) : changeData.lastValue.toFixed(0)) +
      ')';

    let cssClass = 'neutral';

    if (changeData.percent > 0) {
      cssClass = 'positive';
    } else if (changeData.percent < 0) {
      cssClass = 'negative';
    }

    if (metric === 'price') {
      this.priceChangeText = text;
      this.priceChangeSubtext = subtext;
      this.priceChangeClass = cssClass;
    } else {
      this.orderChangeText = text;
      this.orderChangeSubtext = subtext;
      this.orderChangeClass = cssClass;
    }
  }

  createChart(canvasId: string, chartLabel: string, metricField: 'price' | 'orderCount'): any {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;

    if (!canvas) {
      return null;
    }

    const self = this;

    return new Chart(canvas, {
      type: 'bar',
      data: {
        labels: this.months,
        datasets: this.prepareDatasets(metricField)
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: function (evt: any, activeElements: any[]) {
          if (!activeElements || !activeElements.length) {
            return;
          }

          if (self.currentView === 'subsegment') {
            return;
          }

          const clicked = activeElements[0];
          const datasetIndex = clicked._datasetIndex;
          const dataset = (this as any).data.datasets[datasetIndex];

          if (dataset && dataset.label) {
            self.drillToSubsegment(dataset.label);
          }
        },
        hover: {
          mode: 'nearest',
          intersect: true,
          onHover: function (e: any, elements: any[]) {
            const target = e ? e.target : null;
            if (target) {
              target.style.cursor = elements && elements.length ? 'pointer' : 'default';
            }
          }
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#0f172a',
          titleFontColor: '#ffffff',
          bodyFontColor: '#ffffff',
          footerFontColor: '#cbd5e1',
          callbacks: {
            label: (tooltipItem: any, data: any) => {
              const dataset = data.datasets[tooltipItem.datasetIndex || 0];
              const value = Number(dataset.data[tooltipItem.index || 0] || 0);
              return dataset.label + ': ' + (metricField === 'price' ? value.toFixed(2) : value.toFixed(0));
            },
            footer: (tooltipItems: any, data: any) => {
              let total = 0;

              tooltipItems.forEach((item: any) => {
                const dataset = data.datasets[item.datasetIndex || 0];
                total += Number(dataset.data[item.index || 0] || 0);
              });

              return 'Monthly Total: ' + (metricField === 'price' ? total.toFixed(2) : total.toFixed(0));
            }
          }
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            fontColor: '#334155',
            boxWidth: 14,
            padding: 16
          }
        },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              display: false
            },
            ticks: {
              fontColor: '#475569'
            },
            scaleLabel: {
              display: true,
              labelString: 'Months',
              fontColor: '#334155'
            }
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true,
              fontColor: '#475569'
            },
            gridLines: {
              color: 'rgba(148, 163, 184, 0.18)',
              drawBorder: false
            },
            scaleLabel: {
              display: true,
              labelString: chartLabel,
              fontColor: '#334155'
            }
          }]
        }
      }
    });
  }

  renderCharts(): void {
    this.destroyCharts();

    setTimeout(() => {
      this.priceChart = this.createChart(
        'priceChart',
        this.currentView === 'segment' ? 'Price by Segment' : 'Price by Subsegment',
        'price'
      );

      this.orderChart = this.createChart(
        'orderChart',
        this.currentView === 'segment' ? 'Order Count by Segment' : 'Order Count by Subsegment',
        'orderCount'
      );
    }, 50);
  }

  getTotalPrice(): number {
    return this.summaryTableData.reduce((sum, x) => sum + Number(x.price || 0), 0);
  }

  getTotalOrders(): number {
    return this.summaryTableData.reduce((sum, x) => sum + Number(x.orderCount || 0), 0);
  }

  getTotalRowCount(): number {
    return this.summaryTableData.reduce((sum, x) => sum + Number(x.rowCount || 0), 0);
  }

  exportTableToExcel(): void {
    const rows = this.summaryTableData.map((x, index) => ({
      'No': index + 1,
      [this.currentView === 'segment' ? 'Segment' : 'Subsegment']: x.name,
      'Price': Number(x.price || 0).toFixed(2),
      'Order Count': Number(x.orderCount || 0),
      'Record Count': Number(x.rowCount || 0)
    }));

    rows.push({
      'No': '',
      [this.currentView === 'segment' ? 'Segment' : 'Subsegment']: 'TOTAL',
      'Price': this.getTotalPrice().toFixed(2),
      'Order Count': this.getTotalOrders(),
      'Record Count': this.getTotalRowCount()
    } as any);

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, 'Analytics');
    XLSX.writeFile(
      wb,
      this.currentView === 'segment'
        ? 'segment-summary.xlsx'
        : 'subsegment-summary-' + (this.selectedSegment || 'view') + '.xlsx'
    );
  }
}
